import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ClearTwoToneIcon from '@mui/icons-material/ClearTwoTone';
import moment from 'moment';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import { theme as colorTheme } from '../../../Convoze.theme';

const SCHEDULE_DATE_VALUE_FORMAT = 'YYYY-MM-DD';
const SCHEDULE_DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
const primary = colorTheme('palette', 'primary').value;

const createInitialForm = () => ({
  fullName: '',
  mobile: '',
  sapCode: '',
  panNumber: '',
  scheduleDate: '',
  scheduleTime: '',
  agentMappingId: ''
});

const buildScheduleIso = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return '';
  const composed = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(composed.getTime()) ? '' : composed.toISOString();
};

const getApiErrorMessage = (error, fallback) => (
  error?.response?.data?.status?.value
  || error?.response?.data?.message
  || error?.message
  || fallback
);

export default function CandidateFormDrawer({ open, onClose, onSave, onMessage, token, agents = [] }) {
  const [form, setForm] = useState(createInitialForm());
  const [resumeFile, setResumeFile] = useState(null);
  const [createdCandidate, setCreatedCandidate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const resumeInputRef = useRef(null);

  const handleChange = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const chooseResume = (event) => {
    setResumeFile(event.target.files?.[0] || null);
  };

  const resetForm = () => {
    setForm(createInitialForm());
    setResumeFile(null);
    setCreatedCandidate(null);
    if (resumeInputRef.current) resumeInputRef.current.value = '';
  };

  const uploadResumeForCandidate = async (candidateId) => {
    if (!resumeFile || !candidateId) return;

    const resumeFormData = new FormData();
    resumeFormData.append('resume', resumeFile);
    resumeFormData.append('candidateId', candidateId);

    await axiosInstance.post(nipponBotApiEndpoints.resumeUpload, resumeFormData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.fullName.trim()) return onMessage?.('Candidate full name is required.', 'error');
    if (!form.mobile.trim()) return onMessage?.('Candidate mobile is required.', 'error');
    if (!form.sapCode.trim()) return onMessage?.('SAP Code is required.', 'error');
    if (!resumeFile) return onMessage?.('Resume upload is required.', 'error');
    if (!form.agentMappingId) return onMessage?.('Select a language.', 'error');

    const scheduledAt = buildScheduleIso(form.scheduleDate, form.scheduleTime);
    if (!scheduledAt) return onMessage?.('Select a valid interview date and time.', 'error');
    if (new Date(scheduledAt).getTime() <= Date.now()) return onMessage?.('Please select a future date and time for the interview.', 'error');

    setSubmitting(true);
    try {
      const authHeaders = { Authorization: `Bearer ${token}` };
      let resolvedCandidate = createdCandidate;

      if (!resolvedCandidate?.id) {
        const response = await axiosInstance.post(nipponBotApiEndpoints.candidateCreate, {
          fullName: form.fullName,
          mobile: form.mobile,
          sapCode: form.sapCode,
          panNumber: form.panNumber
        }, {
          headers: authHeaders
        });
        resolvedCandidate = response?.data?.data;
        if (!resolvedCandidate?.id) throw new Error('Candidate was not created successfully.');
        setCreatedCandidate(resolvedCandidate);
        await uploadResumeForCandidate(resolvedCandidate.id);
      }

      await axiosInstance.post(nipponBotApiEndpoints.outboundSchedules, {
        agentMappingId: form.agentMappingId,
        scheduledAt,
        waitIntervalSeconds: 60,
        maxConcurrentCalls: 1,
        includePreviousCallSummary: false,
        candidateIds: [resolvedCandidate.id]
      }, { headers: authHeaders });

      onMessage?.('Candidate uploaded and interview scheduled successfully.', 'success');
      resetForm();
      onSave?.(resolvedCandidate);
      onClose?.();
    } catch (error) {
      onMessage?.(getApiErrorMessage(error, 'Failed to create candidate.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: { xs: '100vw', sm: '80vw', md: '50vw', lg: '50vw' },
          maxWidth: '100vw',
          backgroundColor: '#fff',
          boxShadow: 24,
          zIndex: 1300,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fff',
            zIndex: 999
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>Add Candidate</Typography>
            <Typography variant="body2" color="text.secondary">Create a new candidate and schedule an interview</Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <ClearTwoToneIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 2, minHeight: 0 }}>
          <TextField
            label="Hiring Manager SAP Code"
            value={form.sapCode}
            onChange={handleChange('sapCode')}
            fullWidth
            required
            sx={{ mb: 3 }}
          />

          <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>Candidate Details</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the candidate's information below.
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
            <TextField label="Candidate Name" value={form.fullName} onChange={handleChange('fullName')} fullWidth required />
            <TextField label="Mobile Number" value={form.mobile} onChange={handleChange('mobile')} fullWidth required />
            <TextField label="PAN Number" value={form.panNumber} onChange={handleChange('panNumber')} fullWidth />
            <Button variant="outlined" component="label" sx={{ textTransform: 'none', justifyContent: 'flex-start', minHeight: 56 }} fullWidth>
              {resumeFile ? `Resume: ${resumeFile.name}` : 'Upload Resume *'}
              <input ref={resumeInputRef} hidden type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" onChange={chooseResume} />
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Resume upload is required so the candidate profile can be extracted automatically.
          </Alert>

          <Box sx={{ borderTop: '1px solid #e0e0e0', my: 3 }} />

          <Typography variant="h6" fontWeight={500} sx={{ mb: 1 }}>Schedule Interview</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the interview date, time, and language.
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label="Interview Date"
                format={SCHEDULE_DATE_DISPLAY_FORMAT}
                value={form.scheduleDate ? moment(form.scheduleDate, SCHEDULE_DATE_VALUE_FORMAT) : null}
                onChange={(value) => setForm((current) => ({
                  ...current,
                  scheduleDate: value && value.isValid() ? value.format(SCHEDULE_DATE_VALUE_FORMAT) : ''
                }))}
                disablePast
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
            <TextField
              type="time"
              label="Interview Time"
              value={form.scheduleTime}
              onChange={handleChange('scheduleTime')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="candidate-agent-label">Language</InputLabel>
              <Select
                labelId="candidate-agent-label"
                label="Language"
                value={form.agentMappingId}
                onChange={handleChange('agentMappingId')}
              >
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>{agent.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ textTransform: 'none', bgcolor: primary, '&:hover': { bgcolor: primary } }}
            >
              {submitting ? <><CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />Scheduling...</> : 'Upload & Schedule'}
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box
        onClick={handleClose}
        sx={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: 1200,
          display: { xs: 'none', md: 'block' }
        }}
      />
    </>
  );
}
